
import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../CommonComponents/CustomSelect";
import { onKeyDown, showToastMessage } from "../../../utils/commonFunctions";
import { Dropdown, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MissionStore } from "../../../store/MissionSlice";
import CustomToolTip from "../../CommonComponents/CustomToolTip";

interface EdgeProps {
  allEdgeDevices: any[];
  usedEdges: string[];
  setDeleteCatalouge: (catalogue: any) => void;
  onEdit: (catalog: any, catalogId: string) => void;
  onClone: (catalog: any, catalogId: string) => void;
}

const Edge = ({ allEdgeDevices, usedEdges, setDeleteCatalouge, onEdit, onClone }: EdgeProps) => {

  const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);

  const [filterEdgeList, setFilterEdgeList] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [filterOptionsValues, setFilterOptionsValues] = useState(
    {
      'GPU': null,
      'Memory': {
        min: '',
        max: ''
      },
      'Memory Type': null,
      'Storage': {
        min: '',
        max: ''
      },
      'Power': {
        min: '',
        max: ''
      },
      'CPU': {
        min: '',
        max: ''
      }
    }
  );

  const EdgeList = useMemo(() => allEdgeDevices, [allEdgeDevices]);

  useEffect(() => {
    setFilterEdgeList(allEdgeDevices)
    let options = {
      'GPU': [],
      'Memory Type': [],
    }
    allEdgeDevices.forEach((edge) => {
      if (edge?.['product-cookie']?.['GPU'] !== 'n/a') {
        options = {
          ...options,
          'GPU': filter_unique_values([
            ...options?.['GPU'],
            { label: edge?.['product-cookie']?.['GPU'], value: edge?.['product-cookie']?.['GPU'] }
          ]),
          'Memory Type': filter_unique_values([
            ...options?.['Memory Type'],
            { label: edge?.['product-cookie']?.['Memory Type'], value: edge?.['product-cookie']?.['Memory Type'] }
          ]),
        }
        setFilterOptions(options)
      }
    })
  }, [allEdgeDevices]);

  const filter_unique_values = (arr) => {
    const uniqueValues = {};
    const uniqueArr = arr.filter(item => {
      if (!uniqueValues[item.label]) {
        uniqueValues[item.label] = true;
        return true;
      }
      return false;
    });

    return uniqueArr;
  }


  const reset_filter = () => {
    setFilterOptionsValues(
      {
        'GPU': null,
        'Memory': {
          min: '',
          max: ''
        },
        'Memory Type': null,
        'Storage': {
          min: '',
          max: ''
        },
        'Power': {
          min: '',
          max: ''
        },
        'CPU': {
          min: '',
          max: ''
        }
      }
    )
    setFilterEdgeList(EdgeList)
  }

  const hasAccess = (uniqueId: string) => {
    if (usedEdges.includes(uniqueId)) {
      showToastMessage('error', 'Edge is already used in a Satellite. Cannot be edited.');
      return false;
    } else {
      return true;
    }
  };

  // ✅ Centralized filter function
  const applyFilters = (devices: any[], filters: any) => {
    let result = devices;

    // CPU
    if (filters?.CPU?.min !== '')
      result = result.filter(d => +d?.["product-cookie"]?.['CPU Cores'] >= +filters.CPU.min);
    if (filters?.CPU?.max !== '' && +filters.CPU.max >= +filters.CPU.min)
      result = result.filter(d => +d?.["product-cookie"]?.['CPU Cores'] <= +filters.CPU.max);

    // Memory
    if (filters?.Memory?.min !== '')
      result = result.filter(d => +d?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filters.Memory.min);
    if (filters?.Memory?.max !== '' && +filters.Memory.max >= +filters.Memory.min)
      result = result.filter(d => +d?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +filters.Memory.max);

    // Storage
    if (filters?.Storage?.min !== '')
      result = result.filter(d => +d?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filters.Storage.min);
    if (filters?.Storage?.max !== '' && +filters.Storage.max >= +filters.Storage.min)
      result = result.filter(d => +d?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filters.Storage.max);

    // Power
    if (filters?.Power?.min !== '')
      result = result.filter(d => +d?.["max_power_consumption_in_watts"] >= +filters.Power.min);
    if (filters?.Power?.max !== '' && +filters.Power.max >= +filters.Power.min)
      result = result.filter(d => +d?.["max_power_consumption_in_watts"] <= +filters.Power.max);

    // GPU
    if (filters?.GPU)
      result = result.filter(d => d?.["product-cookie"]?.['GPU'] === filters.GPU.value);

    // Memory Type
    if (filters?.['Memory Type'])
      result = result.filter(d => d?.["product-cookie"]?.['Memory Type'] === filters['Memory Type'].value);

    return result;
  };


  return (
    <div className='d-flex flex-wrap w-100 flex-1'>
      <div className='d-flex w-100 h-100'>
        <div className='w-20 bg-transparent border-right px-2 py-3 d-flex flex-column justify-content-between'>
          <div className="d-flex flex-column gap-3">
            <div className='d-flex flex-column gap-2'>
              <span className="">CPU / RT Cores</span>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Minimum'
                  value={filterOptionsValues['CPU']?.['min']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'CPU': {
                        min: e.target.value,
                        max: filterOptionsValues['CPU']?.['max']
                      }
                    })
                    let filter_devices: any = e.target.value !== '' ? EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +e.target.value) : EdgeList
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
                <span>-</span>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Maximum'
                  value={filterOptionsValues['CPU']?.['max']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'CPU': {
                        min: filterOptionsValues['CPU']?.['min'],
                        max: e.target.value
                      }
                    })
                    let filter_devices = e.target.value !== '' ? EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +e.target.value) : EdgeList
                    filter_devices = (filterOptionsValues['CPU']?.['min'] !== '' && +filterOptionsValues['CPU']?.['min'] <= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
              </div>
            </div>
            <div className='d-flex flex-column gap-2'>
              <span className="">Memory</span>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Minimum'
                  value={filterOptionsValues['Memory']?.['min']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Memory': {
                        min: e.target.value,
                        max: filterOptionsValues['Memory']?.['max']
                      }
                    })
                    let filter_devices: any = EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +e.target.value)
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
                <span>-</span>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Maximum'
                  value={filterOptionsValues['Memory']?.['max']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Memory': {
                        min: filterOptionsValues['Memory']?.['min'],
                        max: e.target.value
                      }
                    })
                    let filter_devices: any = e.target.value !== '' ? EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +e.target.value) : EdgeList
                    filter_devices = (filterOptionsValues['Memory']?.['min'] !== '' && +filterOptionsValues['Memory']?.['min'] <= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
              </div>
            </div>
            <div className='d-flex flex-column gap-2'>
              <span className="">Storage</span>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Minimum'
                  value={filterOptionsValues['Storage']?.['min']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Storage': {
                        min: e.target.value,
                        max: filterOptionsValues['Storage']?.['max']
                      }
                    })
                    let filter_devices = EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +e.target.value)
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +e.target.value) ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= filterOptionsValues['Storage']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
                <span>-</span>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Maximum'
                  value={filterOptionsValues['Storage']?.['max']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Storage': {
                        min: filterOptionsValues['Storage']?.['min'],
                        max: e.target.value
                      }
                    })
                    let filter_devices = e.target.value !== '' ? EdgeList?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +e.target.value) : EdgeList
                    filter_devices = (filterOptionsValues['Storage']?.['min'] !== '' && +filterOptionsValues['Storage']?.['min'] <= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] <= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
              </div>
            </div>
            {Object.keys(filterOptions).length > 0 && Object.keys(filterOptions).map((key, index) => {
              return (
                <div className='d-flex flex-column gap-2' key={index}>
                  <span className="">{key}</span>
                  <CustomSelect
                    background='rgba(10, 10, 10, 0.65)'
                    options={filterOptions?.[key]}
                    value={filterOptionsValues[key]}
                    placeholder={"Select " + key}
                    onChange={(e) => {
                      setFilterOptionsValues({
                        ...filterOptionsValues,
                        [key]: e
                      })
                      let filter_devices = EdgeList?.filter((device: any) => device?.["product-cookie"][key] === e.value)
                      filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                      filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                      filter_devices = filterOptionsValues['Power']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                      filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +filterOptionsValues['Power']?.['min']) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                      filter_devices = (key !== 'GPU' && filterOptionsValues['GPU'] !== null) ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                      filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                      filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['max']) : filter_devices
                      filter_devices = (key !== 'Memory Type' && filterOptionsValues['Memory Type'] !== null) ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                      filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                      filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                      setFilterEdgeList(filter_devices)
                    }}
                  />
                </div>
              )
            })}
            <div className='d-flex flex-column gap-2'>
              <span className="">Peak Power</span>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Minimum'
                  value={filterOptionsValues['Power']?.['min']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Power': {
                        min: e.target.value,
                        max: filterOptionsValues['Power']?.['max']
                      }
                    })
                    let filter_devices = EdgeList?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +e.target.value)
                    filter_devices = (filterOptionsValues['Power']?.['max'] !== '' && +filterOptionsValues['Power']?.['max'] >= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +filterOptionsValues['Power']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
                <span>-</span>
                <Form.Control
                  type="number"
                  min={0}
                  placeholder='Maximum'
                  value={filterOptionsValues['Power']?.['max']}
                  onKeyDown={onKeyDown}
                  onChange={(e) => {
                    setFilterOptionsValues({
                      ...filterOptionsValues,
                      'Power': {
                        min: filterOptionsValues['Power']?.['min'],
                        max: e.target.value
                      }
                    })
                    let filter_devices = e.target.value !== '' ? EdgeList?.filter((device: any) => +device?.["max_power_consumption_in_watts"] <= +e.target.value) : EdgeList
                    filter_devices = (filterOptionsValues['Power']?.['min'] !== '' && +filterOptionsValues['Power']?.['min'] <= +e.target.value) ? filter_devices?.filter((device: any) => +device?.["max_power_consumption_in_watts"] >= +filterOptionsValues['Power']?.['min']) : filter_devices
                    filter_devices = filterOptionsValues['CPU']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] >= +filterOptionsValues['CPU']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['CPU']?.['max'] !== '' && +filterOptionsValues['CPU']?.['max'] >= +filterOptionsValues['CPU']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['CPU Cores'] <= +filterOptionsValues['CPU']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['GPU'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['GPU'] === filterOptionsValues['GPU']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Memory']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Memory']?.['max'] !== '' && +filterOptionsValues['Memory']?.['max'] >= +filterOptionsValues['Memory']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Memory']?.split(' ')[0] >= +filterOptionsValues['Memory']?.['max']) : filter_devices
                    filter_devices = filterOptionsValues['Memory Type'] !== null ? filter_devices?.filter((device: any) => device?.["product-cookie"]?.['Memory Type'] === filterOptionsValues['Memory Type']?.['value']) : filter_devices
                    filter_devices = filterOptionsValues['Storage']?.['min'] !== '' ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] >= +filterOptionsValues['Storage']?.['min']) : filter_devices
                    filter_devices = (filterOptionsValues['Storage']?.['max'] !== '' && +filterOptionsValues['Storage']?.['max'] >= +filterOptionsValues['Storage']?.['min']) ? filter_devices?.filter((device: any) => +device?.["product-cookie"]?.['Storage']?.split(' ')[0] <= +filterOptionsValues['Storage']?.['max']) : filter_devices
                    setFilterEdgeList(filter_devices)
                  }} />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <button className='btn btn-outline-primary px-8 fs-18' onClick={reset_filter}>Reset</button>
          </div>
        </div>
        <span className='border-left-3 h-100'></span>
        <div className="w-80 d-flex flex-column flex-1 pop-scroll-3xl py-5 ps-3 gap-4">
          <div className="catalog__cards__grid__container">
            {filterEdgeList?.length > 0 && filterEdgeList.map((data, i) => (
              <div className="catalog__card__container" key={data?.["uniqueId"]}>
                <div className="catalog__card__header">
                  <div className="d-flex flex-column">
                    <span className="card__text__container card__header_title">
                      <CustomToolTip title={data?.["name"]}>
                        {data?.["name"]}
                      </CustomToolTip>
                    </span>
                  </div>
                  <div className="card__icon__container">
                    <Dropdown drop={"end"}>
                      <Dropdown.Toggle variant="transparent text-white fs-20">
                        <i className="fe fe-more-vertical"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {data?.['owner-org'] === userData?.['owner_org'] &&
                          <Dropdown.Item
                            eventKey="0"
                            onClick={() => {
                              if (hasAccess(data?.['uniqueId'])) {
                                onEdit(data, data?.['uniqueId']);
                              }
                            }}
                          >
                            Edit
                          </Dropdown.Item>
                        }
                        <Dropdown.Item
                          eventKey="1"
                          onClick={() => {
                            onClone(data, data?.['uniqueId']);
                          }}
                        >
                          Clone
                        </Dropdown.Item>
                        {data?.['owner-org'] === userData?.['owner_org'] &&
                          <Dropdown.Item
                            eventKey="2"
                            onClick={() => {
                              if (hasAccess(data?.['uniqueId'])) {
                                setDeleteCatalouge(data);
                              }
                            }}
                          >
                            Delete
                          </Dropdown.Item>
                        }
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
                <div className="catalog__card__body">
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">{data['product-cookie']['Size']} U</span>
                    <span className="card__text__container card__body__subtitle">Size</span>
                  </div>
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">
                      <CustomToolTip title={data['product-cookie']['CPU Cores']}>
                        {data['product-cookie']['CPU Cores']}
                      </CustomToolTip>
                    </span>
                    <span className="card__text__container card__body__subtitle">CPU Cores</span>
                  </div>
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">
                      <CustomToolTip title={data['product-cookie']['GPU']}>
                        {data['product-cookie']['GPU']}
                      </CustomToolTip>
                    </span>
                    <span className="card__text__container card__body__subtitle">GPU</span>
                  </div>
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">
                      <CustomToolTip title={data['product-cookie']['Memory']}>
                        {data['product-cookie']['Memory']}
                      </CustomToolTip>
                    </span>
                    <span className="card__text__container card__body__subtitle">Memory</span>
                  </div>
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">
                      <CustomToolTip title={data['product-cookie']['Memory Type']}>
                        {data['product-cookie']['Memory Type']}
                      </CustomToolTip>
                    </span>
                    <span className="card__text__container card__body__subtitle">Memory Type</span>
                  </div>
                  <div className="card__body__text__container">
                    <span className="card__text__container card__body_title">
                      <CustomToolTip title={data['product-cookie']['Storage']}>
                        {data['product-cookie']['Storage']} wjehbfjhwehf wkjvjkc
                      </CustomToolTip>
                    </span>
                    <span className="card__text__container card__body__subtitle">Storage</span>
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edge;
